import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Lightning, ShieldCheck, Lock } from "@phosphor-icons/react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SubpageHero from "@/components/site/SubpageHero";
import ReserveModal from "@/components/site/ReserveModal";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import useSeo from "@/hooks/useSeo";
import { selectInterest } from "@/lib/inquiry";
import { FAQS, faqJsonLd } from "@/lib/faqs";
import { PRICES } from "@/lib/visibility";

const GBP_FAQS = FAQS.filter((f) => f.gbp);

const INCLUDED = [
  "Business details, hours, contact information, website links, and service areas",
  "Primary and secondary category research and selection",
  "Business description and your list of services",
  "Upload of your logo, cover image, and the photos you provide",
  "A review-request link you can text or email to customers",
  "Verification guidance whenever Google requires it",
  "A brief completion summary of what was set up or fixed",
];

const STEPS = [
  ["Pay once", `$${PRICES.gbpSetup} per location, through Stripe. No subscription starts and nothing else is required.`],
  ["Grant manager access", "You keep ownership. We walk you through adding WebJesters as a manager — we never ask for your Google password."],
  ["We set up or clean up", "New profile or existing: details, categories, description, services, photos, and the review link."],
  ["Completion summary", "You get a short written summary of what changed, plus verification guidance if Google asks for it."],
];

const GOOD_TO_KNOW = [
  ["You own it", "The profile stays in your Google account. We work through manager access that you grant and can remove any time."],
  ["Verification is Google's call", "Some profiles need verification by video, postcard, or phone. We guide you through it, but Google decides — and it can require your participation."],
  ["No guarantees", "We don't guarantee verification, rankings, or leads. We make your profile complete and accurate; Google controls the rest."],
  ["Out of scope", "Suspension appeals, ownership disputes, and complicated duplicate-listing issues need a separate assessment and quote."],
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Google Business Profile Setup", item: "https://webjesters.com/google-business-profile" },
      ],
    },
    {
      "@type": "Service",
      "@id": "https://webjesters.com/google-business-profile#service",
      name: "Google Business Profile Setup or Cleanup",
      serviceType: "Google Business Profile setup and optimization",
      provider: { "@id": "https://webjesters.com/#organization" },
      areaServed: { "@type": "Country", name: "United States" },
      url: "https://webjesters.com/google-business-profile",
      description:
        "One-time Google Business Profile setup or cleanup for local service businesses: $299 per location. Covers business details, hours, contact info, website links, service areas, category research, business description and services, client-provided photos, a review-request link, verification guidance, and a brief completion summary. No subscription and no website purchase required.",
      offers: { "@type": "Offer", name: "Google Business Profile setup or cleanup", price: "299", priceCurrency: "USD" },
    },
    faqJsonLd(GBP_FAQS),
  ],
};

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
    <span className="block w-8 h-px bg-brand-700" />
    {children}
  </div>
);

export default function GoogleBusinessProfilePage() {
  const [buyOpen, setBuyOpen] = useState(false);
  useSeo({
    title: "Google Business Profile Setup or Cleanup — $299 One-Time | WebJesters",
    description:
      "Google Business Profile created or cleaned up for $299 one-time per location. Categories, services, photos and a review link. No subscription.",
    path: "/google-business-profile",
    jsonLd: JSON_LD,
  });

  return (
    <main id="main-content" data-testid="gbp-page" className="relative">
      <Navbar />
      <SubpageHero
        crumb="Google Business Profile"
        eyebrow="Google Business Profile · One-time service"
        title="Your Google Business Profile, set up or cleaned up — once."
        intro={`A complete, accurate Google listing is often the first thing a local customer sees. We create a new profile or audit and clean up the one you have, for $${PRICES.gbpSetup} one-time per location. No subscription, no website purchase, and we never ask for your Google password.`}
      >
        <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setBuyOpen(true)}
            data-testid="gbp-hero-buy"
            className="group inline-flex items-center justify-center gap-3 pl-7 pr-2 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          >
            <Lightning size={15} weight="fill" className="text-brand-600" />
            Buy setup · ${PRICES.gbpSetup} one-time
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-600 text-white transition-transform group-hover:translate-x-0.5">
              <ArrowUpRight size={16} weight="bold" />
            </span>
          </button>
          <a
            href="#contact"
            onClick={() => selectInterest("gbp_setup")}
            data-testid="gbp-hero-ask"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white font-medium hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
          >
            Ask a question first
          </a>
        </div>
      </SubpageHero>

      <section id="gbp-details" data-tone="light" data-testid="gbp-details" className="tone-light relative py-14 sm:py-24 bg-[#f6f5f1] border-t border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="gbp-price-card"
            className="lg:col-span-5 relative rounded-3xl border border-zinc-900/10 bg-gradient-to-b from-[#121214] to-[#0a0a0c] overflow-hidden shadow-[0_40px_90px_-30px_rgba(24,24,27,0.5)]"
          >
            <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-brand-500/[0.18] blur-3xl pointer-events-none" />
            <div className="relative p-8 sm:p-10">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] tracking-[0.16em] uppercase text-zinc-300">
                One-time · no subscription · no website required
              </div>
              <div className="mt-7 flex items-baseline gap-x-3">
                <span className="font-display text-6xl sm:text-7xl tracking-tighter text-white leading-none">${PRICES.gbpSetup}</span>
                <span className="text-zinc-400 text-[15px] sm:text-base leading-snug">
                  one-time
                  <span className="block text-[12.5px] text-zinc-400">per business location</span>
                </span>
              </div>
              <p className="mt-7 text-zinc-300 text-[14.5px] sm:text-[15px] leading-relaxed">
                Paid once, up front. Covers either creating a new Google Business Profile or auditing, cleaning up, and
                completing the one you already have. When it&apos;s done, it&apos;s yours — nothing recurring.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => setBuyOpen(true)}
                  data-testid="gbp-buy-cta"
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors duration-200 whitespace-nowrap"
                >
                  <Lightning size={15} weight="fill" className="text-brand-600" />
                  Buy setup · ${PRICES.gbpSetup}
                  <ArrowUpRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href="#contact"
                  onClick={() => selectInterest("gbp_setup")}
                  data-testid="gbp-inquiry-cta"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors duration-200 text-[14px] whitespace-nowrap"
                >
                  Talk to us first
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-zinc-400">
                <span className="inline-flex items-center gap-1.5"><Lock size={12} weight="regular" className="text-zinc-300" />Powered by Stripe</span>
                <span className="text-zinc-700">·</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} weight="regular" className="text-emerald-400/80" />You keep ownership</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 lg:pt-2"
            data-testid="gbp-included"
          >
            <Eyebrow>Included</Eyebrow>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {INCLUDED.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check size={14} weight="bold" className="mt-1 flex-shrink-0 text-brand-700" />
                  <span className="text-[14px] sm:text-[14.5px] text-zinc-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[13px] text-zinc-600 leading-relaxed">
              Works for a brand-new listing or an existing one that&apos;s incomplete, outdated, or inconsistent. Each
              additional location is its own ${PRICES.gbpSetup} setup.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-tone="light" className="tone-light relative py-14 sm:py-24 bg-white border-y border-zinc-200" data-testid="gbp-how-it-works">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-[30px] sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-zinc-900 max-w-2xl">
            Four steps, one payment, nothing recurring.
          </h2>
          <ol className="relative mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            <span aria-hidden className="hidden lg:block absolute top-6 left-[12%] right-[12%] h-px bg-zinc-200" />
            {STEPS.map(([h, p], i) => (
              <li key={h} data-testid={`gbp-step-${i}`} className="relative">
                <span className="relative z-10 inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-900 text-white font-display font-bold text-[15px] ring-8 ring-white">
                  0{i + 1}
                </span>
                <h3 className="mt-5 font-display font-bold text-[18px] sm:text-[19px] leading-snug tracking-tight text-zinc-900">{h}</h3>
                <p className="mt-2.5 text-[14px] sm:text-[14.5px] text-zinc-600 leading-relaxed">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section data-tone="light" className="tone-light relative py-14 sm:py-24 bg-[#f6f5f1]" data-testid="gbp-good-to-know">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Eyebrow>Good to know</Eyebrow>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GOOD_TO_KNOW.map(([h, p]) => (
              <div key={h} className="card-light rounded-3xl p-6">
                <h2 className="text-[15.5px] font-medium text-zinc-900 leading-snug">{h}</h2>
                <p className="mt-2.5 text-[14px] text-zinc-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>

          <div
            data-testid="gbp-upsell-visibility"
            className="mt-10 sm:mt-14 rounded-3xl bg-zinc-900 text-white p-6 sm:p-9 grid lg:grid-cols-12 gap-6 items-center relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" />
            <div className="relative lg:col-span-8">
              <div className="text-[11px] tracking-[0.22em] uppercase text-brand-400">Want it kept active too?</div>
              <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight text-white">
                Local Visibility posts to it every month.
              </h2>
              <p className="mt-3 text-[14.5px] text-zinc-300 leading-relaxed max-w-2xl">
                Four content pieces a month from your job photos and clips — prepared with captions, simple branding, and
                light edits where needed — published to your Google Business Profile and Facebook or Instagram, plus profile upkeep — ${PRICES.monthly}/month. If you&apos;ve already paid for this
                ${PRICES.gbpSetup} setup, the ${PRICES.onboarding} onboarding fee is waived when you add it.
              </p>
            </div>
            <div className="relative lg:col-span-4 flex lg:justify-end">
              <Link
                to="/local-visibility"
                data-testid="gbp-visibility-link"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
              >
                See Local Visibility
                <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FAQ
        items={GBP_FAQS}
        tone="light"
        title="Google Business Profile questions."
        intro="Straight answers on what the one-time setup covers, access, and verification."
      />
      <Contact defaultInterest="gbp_setup" />
      <Footer />
      <ReserveModal open={buyOpen} onClose={() => setBuyOpen(false)} packageId="gbp_setup" />
    </main>
  );
}
